import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, ChevronRight } from "lucide-react";
import MemberSubscriptions from "@/components/MemberSubscriptions";

const PLANS = [
  { key: "coaching_1sem_monthly", label: "Coaching 1 séance/semaine", sessions: 1 },
  { key: "coaching_2sem_monthly", label: "Coaching 2 séances/semaine", sessions: 2 },
  { key: "coaching_3sem_monthly", label: "Coaching 3 séances/semaine", sessions: 3 },
  { key: "libre_1sem_monthly", label: "Libre 1 séance/semaine", sessions: 1 },
  { key: "libre_2sem_monthly", label: "Libre 2 séances/semaine", sessions: 2 },
  { key: "libre_3sem_monthly", label: "Libre 3 séances/semaine", sessions: 3 },
  { key: null, label: "Aucun forfait (inactif)", sessions: 0 },
];

export default function MembersManager() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewSubscriptionsModal, setViewSubscriptionsModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const [newMember, setNewMember] = useState({
    name: "",
    email: "",
    password: "",
    plan: null,
    sessions_per_week: 0,
    send_welcome_email: true,
  });

  useEffect(() => {
    if (user?.role === "admin") {
      loadMembers();
    }
  }, [user]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/members");
      setMembers(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des membres");
    } finally {
      setLoading(false);
    }
  };

  const createMember = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/members", newMember);
      toast.success("Membre créé avec succès !");
      setCreateModalOpen(false);
      setNewMember({ name: "", email: "", password: "", plan: null, sessions_per_week: 0, send_welcome_email: true });
      loadMembers();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const updateMemberPlan = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    try {
      await api.put(`/admin/members/${selectedMember.id}/plan`, {
        plan: selectedMember.active_plan,
        sessions_per_week: selectedMember.sessions_per_week,
      });
      toast.success("Forfait mis à jour !");
      setEditModalOpen(false);
      setSelectedMember(null);
      loadMembers();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const handlePlanChange = (planKey, isNew = false) => {
    const plan = PLANS.find(p => p.key === planKey);
    if (isNew) {
      setNewMember({ ...newMember, plan: planKey, sessions_per_week: plan?.sessions || 0 });
    } else {
      setSelectedMember({ ...selectedMember, active_plan: planKey, sessions_per_week: plan?.sessions || 0 });
    }
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;
    
    setDeleteLoading(true);
    try {
      const res = await api.delete(`/admin/members/${memberToDelete.id}`);
      toast.success(res.data.message || "Membre supprimé");
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      loadMembers();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Erreur lors de la suppression");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (user?.role !== "admin") {
    return <div className="p-8 text-center text-stone-600">Accès réservé aux administrateurs</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-light text-stone-900">Gestion des Membres</h2>
          <p className="text-sm text-stone-600 mt-1">{members.length} membre(s) total</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)} className="bg-[#8B7D6B] hover:bg-[#7A6C5B]">
          + Créer un membre
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-stone-600">Chargement...</div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-600">Nom</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-600">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-600">Forfait</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-600">Séances/sem</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-600">Résa.</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-stone-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {members.map((member) => (
                  <tr key={member.id} className="hover:bg-stone-50/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-stone-900">{member.name}</td>
                    <td className="px-6 py-4 text-sm text-stone-600">{member.email}</td>
                    <td className="px-6 py-4 text-sm text-stone-600">
                      {member.active_subscriptions_count > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {member.active_subscriptions_count}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-stone-400">
                          0
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-600">{member.sessions_per_week}</td>
                    <td className="px-6 py-4 text-sm text-stone-600">{member.total_bookings}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedMember(member);
                            setViewSubscriptionsModal(true);
                          }}
                          className="text-xs h-8 border-[#3b82f6] text-[#3b82f6] hover:bg-[#3b82f6] hover:text-white"
                        >
                          Forfaits
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedMember(member);
                            setEditModalOpen(true);
                          }}
                          className="text-[#8B7D6B] hover:text-[#7A6C5B]"
                        >
                          Modifier
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteClick(member)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {members.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-stone-500">
                      Aucun membre pour le moment
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3 px-2">
            {members.length === 0 ? (
              <div className="rounded-lg border border-stone-200 bg-white p-8 text-center">
                <p className="text-sm text-stone-500">Aucun membre pour le moment</p>
              </div>
            ) : (
              members.map((member) => (
                <div
                  key={member.id}
                  className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-stone-900 truncate">{member.name}</h3>
                      <p className="text-xs text-stone-500 truncate mt-0.5">{member.email}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(member)}
                      className="flex-shrink-0 h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-stone-500">Forfaits</span>
                      {member.active_subscriptions_count > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {member.active_subscriptions_count}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-stone-400">
                          0
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-stone-500">Séances/semaine</span>
                      <span className="font-medium text-stone-900">{member.sessions_per_week}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-stone-500">Réservations</span>
                      <span className="font-medium text-stone-900">{member.total_bookings}</span>
                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedMember(member);
                      setViewSubscriptionsModal(true);
                    }}
                    className="w-full text-xs h-9 border-[#3b82f6] text-[#3b82f6] hover:bg-[#3b82f6] hover:text-white mb-2"
                  >
                    Gérer les forfaits
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedMember(member);
                      setEditModalOpen(true);
                    }}
                    className="w-full text-xs h-9 border-[#8B7D6B] text-[#8B7D6B] hover:bg-[#8B7D6B] hover:text-white"
                  >
                    Modifier le forfait (ancien)
                  </Button>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Create Member Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-light">Créer un nouveau membre</DialogTitle>
            <DialogDescription>
              Un email avec les identifiants sera envoyé automatiquement au membre.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createMember} className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe (optionnel)</Label>
              <Input
                id="password"
                type="password"
                value={newMember.password}
                onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                placeholder="Laissez vide pour génération automatique"
                className="mt-1.5"
              />
              <p className="text-xs text-stone-500 mt-1">
                Si laissé vide, un mot de passe sera généré et envoyé par email
              </p>
            </div>
            <div>
              <Label htmlFor="plan">Forfait</Label>
              <Select value={newMember.plan || ""} onValueChange={(val) => handlePlanChange(val || null, true)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Sélectionnez un forfait" />
                </SelectTrigger>
                <SelectContent>
                  {PLANS.map((plan) => (
                    <SelectItem key={plan.key || "none"} value={plan.key || "none"}>
                      {plan.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateModalOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#8B7D6B] hover:bg-[#7A6C5B]">
                Créer le membre
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Plan Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-light">Modifier le forfait</DialogTitle>
            <DialogDescription>
              {selectedMember?.name} ({selectedMember?.email})
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={updateMemberPlan} className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-plan">Forfait</Label>
              <Select 
                value={selectedMember?.active_plan || ""} 
                onValueChange={(val) => handlePlanChange(val || null, false)}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Sélectionnez un forfait" />
                </SelectTrigger>
                <SelectContent>
                  {PLANS.map((plan) => (
                    <SelectItem key={plan.key || "none"} value={plan.key || "none"}>
                      {plan.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg bg-stone-50 p-4">
              <p className="text-sm text-stone-600">
                Séances par semaine : <span className="font-semibold text-stone-900">{selectedMember?.sessions_per_week}</span>
              </p>
              <p className="text-sm text-stone-600 mt-1">
                Total réservations : <span className="font-semibold text-stone-900">{selectedMember?.total_bookings}</span>
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditModalOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#8B7D6B] hover:bg-[#7A6C5B]">
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{memberToDelete?.name}</strong> ?
              <br /><br />
              Cette action est <strong>irréversible</strong> et supprimera :
              <ul className="mt-2 list-disc list-inside text-sm">
                <li>Le compte du membre</li>
                <li>Toutes ses réservations futures</li>
                <li>Son historique d'activité</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoading ? "Suppression..." : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Subscriptions Modal */}
      <Dialog open={viewSubscriptionsModal} onOpenChange={setViewSubscriptionsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-light">
              Forfaits de {selectedMember?.name}
            </DialogTitle>
            <DialogDescription>
              Gérez les forfaits multiples et visualisez les quotas cumulés
            </DialogDescription>
          </DialogHeader>
          
          {selectedMember && (
            <MemberSubscriptions 
              memberId={selectedMember.id} 
              memberName={selectedMember.name}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
